import { ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import {
  AccountSettings,
  BillingSettings,
  ProfileSettings,
} from '@/components/settings/account-settings';
import { BrandingSettings } from '@/components/settings/branding-settings';
import {
  ChannelSettings,
  FeeSettings,
  LimitSettings,
  PickupSettings,
} from '@/components/settings/commerce-settings';
import { DomainSettings } from '@/components/settings/domain-settings';
import { ImportSettings } from '@/components/settings/import-settings';
import { LocationSettings } from '@/components/settings/location-settings';
import { MetrcSettings } from '@/components/settings/metrc-settings';
import { StoreSettings } from '@/components/settings/store-settings';
import {
  BlogSettings,
  ChatSettings,
  StorefrontSettings,
} from '@/components/settings/storefront-settings';
import { TaxSettings } from '@/components/settings/tax-settings';
import { TeamSettings } from '@/components/settings/team-settings';
import { VehicleSettings } from '@/components/settings/vehicle-settings';
import { ZoneSettings } from '@/components/settings/zone-settings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/lib/auth';
import {
  SETTINGS_GROUP_ORDER,
  SETTINGS_SECTIONS,
  type SettingsSection,
} from '@/lib/settings-sections';
import { cn } from '@/lib/utils';

/**
 * Settings, as a full-screen dialog rather than a route.
 *
 * ctrl-saas made settings a page you navigated to, which meant leaving whatever
 * you were doing. A dialog keeps the console underneath, so changing a tax rate
 * mid-order does not lose the order.
 *
 * Cards are filtered by permission for the reason ctrl-saas gives: the routes
 * behind them 404 without it, and only `owner` holds every one — an unfiltered
 * grid shows a budtender a wall of dead ends.
 */
/**
 * One explicit height for the dialog header, shared by the index and by a
 * section's own view. Both need it fixed rather than content-sized: the body is
 * the scrolling region, and a header that resized with its title would shift
 * the content under it every time you opened or left a section.
 */
const SETTINGS_HEADER_HEIGHT = 'h-24';

/** The back-bar inside a section, kept explicit for the same reason. */
const SETTINGS_SUBHEADER_HEIGHT = 'h-12';

export function SettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { can, activeAccountId } = useAuth();
  const [active, setActive] = useState<SettingsSection | null>(null);

  const visible = SETTINGS_SECTIONS.filter(
    (section) => !section.permission || can(section.permission),
  );

  function close(next: boolean) {
    if (!next) setActive(null);
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      {/*
        Full-screen with breathing room: inset from the viewport rather than
        edge-to-edge, so the console stays visible behind it and the rounded
        corners read as a surface rather than a new page.
      */}
      {/*
        A flex column, not the dialog's default grid.

        With a grid the header and body are auto-sized rows, so the body grew to
        fit its content and the whole panel overflowed instead of scrolling.
        Column flex plus `min-h-0` on the body is what actually gives the scroll
        container a bounded height to scroll within.
      */}
      <DialogContent
        showCloseButton
        className="flex h-[calc(100svh-4rem)] w-[calc(100vw-4rem)] max-w-none flex-col gap-0 overflow-hidden rounded-xl p-0 sm:max-w-none"
      >
        {/*
          Fixed height, and the same one whether the index or a section is
          showing — an auto-height header would jump as the title and
          description changed length between the two.
        */}
        <DialogHeader
          className={cn(SETTINGS_HEADER_HEIGHT, 'shrink-0 justify-center border-b px-8')}
        >
          <DialogTitle className="text-2xl">{active ? active.title : 'Settings'}</DialogTitle>
          <DialogDescription className="line-clamp-1">
            {active
              ? active.description
              : 'Everything about this workspace, its storefront and your own account.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="px-8 py-8">
            {active ? (
              <SectionView
                section={active}
                accountId={activeAccountId}
                onBack={() => setActive(null)}
              />
            ) : (
              <div className="space-y-10">
                {SETTINGS_GROUP_ORDER.map((group) => {
                  const sections = visible.filter((section) => section.group === group);
                  if (!sections.length) return null;

                  return (
                    <section key={group} className="space-y-4">
                      <h2 className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
                        {group}
                      </h2>

                      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {sections.map((section) => (
                          <SettingsCard
                            key={section.key}
                            section={section}
                            onSelect={() => setActive(section)}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}

                {visible.length === 0 && (
                  <p className="text-muted-foreground py-16 text-center text-sm">
                    You do not have access to any settings in this workspace.
                  </p>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function SettingsCard({ section, onSelect }: { section: SettingsSection; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group bg-card text-card-foreground flex w-full items-start gap-4 rounded-lg border p-5 text-left',
        'hover:bg-accent hover:border-foreground/20 transition-colors',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
      )}
    >
      <div className="bg-muted text-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
        <section.icon className="size-4.5" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <h3 className="font-medium">{section.title}</h3>
          <ChevronRight className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5" />
        </div>
        <p className="text-muted-foreground mt-1 text-sm text-balance">{section.description}</p>
      </div>
    </button>
  );
}

/**
 * A section's own screen, under the shared back-bar.
 *
 * Most sections are still placeholders; the ones that are built are named here
 * rather than each card carrying a component reference, so the section list
 * stays a plain data file.
 */
function SectionView({
  section,
  accountId,
  onBack,
}: {
  section: SettingsSection;
  accountId: string | null;
  onBack: () => void;
}) {
  return (
    <div>
      {/*
        Fixed-height toolbar, so entering a section does not nudge the content
        down by however tall a back link happens to render.
      */}
      <div className={cn(SETTINGS_SUBHEADER_HEIGHT, 'flex shrink-0 items-center')}>
        <button
          type="button"
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground text-sm"
        >
          ← All settings
        </button>
      </div>

      {renderSection(section, accountId)}
    </div>
  );
}

function renderSection(section: SettingsSection, accountId: string | null): ReactNode {
  const built = BUILT_SECTIONS[section.key];

  if (!built) return <SectionPlaceholder section={section} />;

  // Every built screen is account-scoped, and the cards are gated on
  // permissions only reachable with an active account -- so this is a guard
  // against an impossible state rather than a case the operator can reach.
  if (!accountId) {
    return <p className="text-muted-foreground text-sm">Choose a workspace first.</p>;
  }

  return built(accountId);
}

/**
 * The sections that have a real screen. Named here rather than on each card so
 * `settings-sections.ts` stays a plain data file with no component imports.
 */
const BUILT_SECTIONS: Record<string, ((accountId: string) => ReactNode) | undefined> = {
  store: (accountId) => <StoreSettings accountId={accountId} />,
  team: (accountId) => <TeamSettings accountId={accountId} />,
  locations: (accountId) => <LocationSettings accountId={accountId} />,
  'delivery-zones': (accountId) => <ZoneSettings accountId={accountId} />,
  vehicles: (accountId) => <VehicleSettings accountId={accountId} />,
  taxes: (accountId) => <TaxSettings accountId={accountId} />,
  branding: (accountId) => <BrandingSettings accountId={accountId} />,
  storefront: (accountId) => <StorefrontSettings accountId={accountId} />,
  chat: (accountId) => <ChatSettings accountId={accountId} />,
  blog: (accountId) => <BlogSettings accountId={accountId} />,
  domains: (accountId) => <DomainSettings accountId={accountId} />,
  fees: (accountId) => <FeeSettings accountId={accountId} />,
  limits: (accountId) => <LimitSettings accountId={accountId} />,
  pickup: (accountId) => <PickupSettings accountId={accountId} />,
  channels: (accountId) => <ChannelSettings accountId={accountId} />,
  account: (accountId) => <AccountSettings accountId={accountId} />,
  profile: () => <ProfileSettings />,
  billing: (accountId) => <BillingSettings accountId={accountId} />,
  metrc: (accountId) => <MetrcSettings accountId={accountId} />,
  import: (accountId) => <ImportSettings accountId={accountId} />,
};

/** Each remaining card's screen is a later phase; this stands in for them. */
function SectionPlaceholder({ section }: { section: SettingsSection }) {
  return (
    <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
      <div className="bg-muted text-foreground flex size-12 items-center justify-center rounded-lg">
        <section.icon className="size-6" />
      </div>
      <h3 className="mt-4 font-medium">{section.title}</h3>
      <p className="text-muted-foreground mt-1 max-w-sm text-sm text-balance">
        {section.description}
      </p>
      <p className="text-muted-foreground mt-4 font-mono text-xs">settings/{section.key}</p>
    </div>
  );
}
